import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ClockIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon as WarningIcon,
  LockClosedIcon,
} from '@heroicons/react/20/solid';

import Sparkline from '../../components/Charts/Sparkline';
import { PROVIDER_COLORS } from '../../components/Charts/chartSetup';
import ErrorState from '../../components/DataDisplay/ErrorState';
import StatCard from '../../components/DataDisplay/StatCard';
import SmashLoader from '../../components/Loader/SmashLoader';
import { getProviderCredits, refreshProviderCredits } from '../../services/ProviderCreditsService';
import {
  balanceHeadline,
  balanceNumber,
  balanceTime,
  balanceTypeLabel,
  formatBalance,
  formatBurnRate,
  formatDateTime,
  formatDaysLeft,
  formatPercentValue,
  formatRelativeTime,
  formatUsedOf,
  humanize,
  usedPercent,
} from '../../utils/helpers/providerBalanceFormat';
import useApiResource from '../../utils/hooks/useApiResource';

const WINDOW_OPTIONS = [7, 30, 90];
const DEFAULT_DAYS = 30;
// Re-reads stored results only; a GET never contacts a provider.
const POLL_INTERVAL_MS = 5 * 60 * 1000;
// Keeps "5 minutes ago" honest between polls.
const CLOCK_TICK_MS = 60 * 1000;
// PROVIDER_COLORS covers the AI providers; the two non-AI families get their own.
const FAMILY_COLORS = { ...PROVIDER_COLORS, cloudinary: '#0EA5E9', resend: '#14B8A6' };
const FALLBACK_COLOR = '#717C9E';
const WARNING_STATUSES = ['past_due', 'incomplete', 'free_disabled', 'canceled', 'unpaid'];

const BADGES = {
  low: { label: 'Low balance', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' },
  failed: { label: 'Check failed', className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200' },
  notConfigured: { label: 'Not configured', className: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
  notChecked: { label: 'Not checked yet', className: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
  unavailable: { label: 'Balance unavailable', className: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200' },
  stale: { label: 'Stale', className: 'border border-amber-400 text-amber-700 dark:border-amber-500/70 dark:text-amber-300' },
  ok: { label: 'OK', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' },
};

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const isPayload = (value) => isPlainObject(value) && Array.isArray(value.results);
const isHttpUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value);
const serverDetail = (requestError) => {
  const detail = requestError?.response?.data?.detail;
  return typeof detail === 'string' ? detail : '';
};
// Both endpoints are superuser-only, so a 401/403 will not change on retry.
const isAccessDenied = (requestError) => [401, 403].includes(requestError?.response?.status);

/** Everything the card, the sort order and the summary need to know about one slot. */
function describe(row, index) {
  const balance = isPlainObject(row.balance) && Object.keys(row.balance).length ? row.balance : null;
  const lastCheck = isPlainObject(row.last_check) ? row.last_check : null;
  const configured = Boolean(row.configured);
  // A slot whose key was removed still carries its last snapshot. That is
  // history, not an alarm: nothing on this page can refresh it.
  const low = configured && Boolean(balance?.low);
  const failed = configured && lastCheck?.status === 'failed';
  // A legacy quota plan reports only a percentage, and that still counts as a balance.
  const unavailable = configured
    && (!balance || (balance.remaining == null && balance.used == null && balance.used_percent == null));
  const notChecked = configured && !lastCheck && !balance;
  const stale = configured && Boolean(row.stale);

  let badges = ['notConfigured'];
  if (configured) {
    badges = [
      low && 'low',
      failed && 'failed',
      notChecked ? 'notChecked' : unavailable && 'unavailable',
      !notChecked && stale && 'stale',
    ].filter(Boolean);
    if (!badges.length) badges = ['ok'];
  }

  return {
    row,
    key: row.provider || `provider-${index}`,
    label: row.label || humanize(row.provider) || 'Provider',
    balance,
    lastCheck,
    configured,
    failed,
    low,
    unavailable,
    notChecked,
    stale,
    badges,
  };
}

/** Low first, then failed, then everything else in the order the server sent. */
const sortItems = (items) => {
  const rank = (item) => (item.low ? 0 : item.failed ? 1 : 2);
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => rank(a.item) - rank(b.item) || a.index - b.index)
    .map(({ item }) => item);
};

/** The server's counts are the contract; the rows, read with the same rules, are only a fallback. */
const summarize = (items, summary) => {
  const pick = (key, fallback) => (Number.isFinite(summary?.[key]) ? summary[key] : fallback);
  const count = (predicate) => items.filter(predicate).length;
  return {
    configured: pick('configured', count((item) => item.configured)),
    low: pick('low', count((item) => item.low)),
    failed: pick('failed', count((item) => item.failed)),
    unavailable: pick('unavailable', count((item) => item.unavailable)),
    stale: pick('stale', count((item) => item.stale)),
  };
};

/** What a screen reader hears once a refresh answers: the outcome, not just "done". */
function refreshAnnouncement(result, provider, fallbackLabel) {
  const rows = isPayload(result) ? result.results.filter(isPlainObject) : [];
  if (provider) {
    const row = rows.find((candidate) => candidate.provider === provider);
    const label = row?.label || fallbackLabel || provider;
    const check = isPlainObject(row?.last_check) ? row.last_check : null;
    if (check?.status === 'failed') return `${label} check failed: ${check.detail || 'no reason was given.'}`;
    return `${label} balance refreshed.`;
  }
  const failed = rows.filter((row) => row.configured && row.last_check?.status === 'failed').length;
  if (!failed) return 'Provider balances refreshed.';
  return `Provider balances refreshed. ${failed} ${failed === 1 ? 'check' : 'checks'} failed.`;
}

function SwapLabel({ busy, idle, active }) {
  // Both labels occupy the same grid cell, so the button never changes width.
  return (
    <span className="grid">
      <span aria-hidden={busy} className={`col-start-1 row-start-1 ${busy ? 'invisible' : ''}`}>{idle}</span>
      <span aria-hidden={!busy} className={`col-start-1 row-start-1 ${busy ? '' : 'invisible'}`}>{active}</span>
    </span>
  );
}

function UsageBar({ percent, label, low, caption, showPercent = true }) {
  const clamped = Math.min(Math.max(percent, 0), 100);
  const tone = percent >= 100 ? 'bg-red-500' : low ? 'bg-amber-500' : 'bg-blue-600 dark:bg-blue-500';
  const percentText = formatPercentValue(percent);
  return (
    <div className="mt-3">
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(clamped)}
        aria-valuetext={`${percentText} used`}
        className="h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"
      >
        <div className={`h-full rounded-full transition-[width] duration-500 ${tone}`} style={{ width: `${clamped}%` }} />
      </div>
      {(showPercent || caption) && (
        <p className="mt-1.5 flex flex-wrap justify-between gap-x-3 text-xs text-slate-500 dark:text-slate-400">
          {showPercent && <span>{percentText} used</span>}
          {caption && <span className="tabular-nums">{caption}</span>}
        </p>
      )}
    </div>
  );
}

function CardHeader({ titleId, label, color, subtitle, badges, muted = false }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className={`h-2.5 w-2.5 shrink-0 rounded-full ${muted ? 'opacity-50' : ''}`}
            style={{ backgroundColor: color }}
          />
          <h2
            id={titleId}
            className={`truncate text-lg font-bold ${muted ? 'text-slate-600 dark:text-slate-300' : 'text-slate-950 dark:text-white'}`}
          >
            {label}
          </h2>
        </div>
        {subtitle && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>}
      </div>
      <div className="flex flex-wrap justify-end gap-1.5">
        {badges.map((badge) => (
          <span
            key={badge}
            className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${BADGES[badge].className}`}
          >
            {BADGES[badge].label}
          </span>
        ))}
      </div>
    </div>
  );
}

function BillingLink({ href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-slate-700"
    >
      Open billing
      <span className="sr-only"> (opens in a new tab)</span>
      <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
    </a>
  );
}

function AccessDenied({ detail }) {
  return (
    <section
      role="alert"
      aria-labelledby="provider-credits-denied-title"
      className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-6 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200"
    >
      <LockClosedIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
      <div>
        <h2 id="provider-credits-denied-title" className="text-base font-semibold">Superuser account required</h2>
        <p className="mt-1 text-sm">
          Provider credits can only be viewed and refreshed from a superuser account. Ask a platform superuser if you
          need these figures.
        </p>
        {detail && <p className="mt-2 text-xs opacity-80">Server response: {detail}</p>}
      </div>
    </section>
  );
}

/** Rows for the details list. Each one appears only when the provider reports it. */
function buildDetails(item, { windowDays, ledgerEnabled }) {
  const { row } = item;
  const balance = item.balance || {};
  const { unit } = balance;
  const details = [];
  const add = (term, value, extra = {}) => {
    if (value != null && value !== '') details.push({ term, value, ...extra });
  };

  add('Plan', balance.plan ? humanize(balance.plan) : null);
  if (balance.status) {
    add('Subscription', humanize(balance.status), {
      warning: WARNING_STATUSES.includes(balance.status),
    });
  }
  add('Resets', formatDateTime(balance.resets_at));

  const remaining = balanceNumber(balance.remaining);
  const limit = balanceNumber(balance.limit);
  const used = balanceNumber(balance.used);
  if (remaining != null && used != null && !(limit > 0)) add('Used', formatBalance(used, unit));
  if (remaining != null) add('Burn rate', formatBurnRate(row.burn_rate_per_day, unit) || 'Not enough history yet');

  const runsOutAt = formatDateTime(row.depletes_at);
  const forecastTitle = runsOutAt ? `Estimated to run out ${runsOutAt}` : undefined;
  const daysLeft = balanceNumber(row.days_left);
  if (remaining != null && remaining <= 0) {
    // Nothing left to forecast, and nothing left to run out before the reset.
    add('Forecast', 'Depleted', { warning: true });
  } else if (daysLeft === 0) {
    // days_left counts from the balance check, so 0 means the forecast date has already passed.
    add('Forecast', 'May already have run out', { warning: true, title: forecastTitle });
  } else if (formatDaysLeft(daysLeft)) {
    const depletesAt = balanceTime(row.depletes_at);
    const resetsAt = balanceTime(balance.resets_at);
    const beforeReset = depletesAt != null && resetsAt != null && depletesAt < resetsAt;
    add(
      'Forecast',
      <>
        {formatDaysLeft(daysLeft)}
        {beforeReset && (
          <span className="mt-0.5 flex items-center justify-end gap-1 text-xs font-semibold text-amber-700 dark:text-amber-300">
            <WarningIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Runs out before the reset
          </span>
        )}
      </>,
      { title: forecastTitle },
    );
  }

  add('Account', balance.account);
  if (typeof balance.auto_reload === 'boolean') {
    const threshold = balanceNumber(balance.auto_reload_threshold_usd);
    const amount = balanceNumber(balance.auto_reload_amount_usd);
    let text = balance.auto_reload ? 'On' : 'Off';
    if (balance.auto_reload && amount != null) {
      text += ` · adds ${formatBalance(amount, 'usd')}`;
      if (threshold != null) text += ` below ${formatBalance(threshold, 'usd')}`;
    }
    add('Auto-reload', text);
  }
  if (balanceNumber(balance.add_on_credits) != null) {
    const until = formatDateTime(balance.add_on_resets_at);
    add('Add-on credits', `${formatBalance(balanceNumber(balance.add_on_credits), 'credits')}${until ? ` · until ${until}` : ''}`);
  }
  if (balanceNumber(balance.premium_credits) != null) {
    add('Premium credits', formatBalance(balanceNumber(balance.premium_credits), 'credits'));
  }
  if (balanceNumber(balance.lite_minutes) != null) {
    add('LITE minutes', `≈ ${formatBalance(balanceNumber(balance.lite_minutes), 'min')}`);
  }
  if (balance.sandbox === true) add('Sandbox mode', 'On');

  // With a `reason` (a sending-only Resend key) the quota could not be read at
  // all: a missing daily limit then means "unknown", not "no daily cap".
  if (balance.type === 'email_quota' && balance.reason == null) {
    const dailyUsed = balanceNumber(balance.daily_used);
    if (balance.daily_limit != null) {
      add('Today', formatUsedOf(dailyUsed, balance.daily_limit, 'emails') || `${formatBalance(balanceNumber(balance.daily_limit), 'emails')} cap`);
    } else {
      add('Today', dailyUsed != null ? `${formatBalance(dailyUsed, 'sent')} · no daily cap` : 'No daily cap');
    }
    add(
      'This month',
      formatUsedOf(balance.monthly_used, balance.monthly_limit, 'emails')
        || (balanceNumber(balance.monthly_used) != null ? `${formatBalance(balanceNumber(balance.monthly_used), 'sent')}` : null),
    );
  }

  const overage = balanceNumber(balance.overage_credits);
  const overageCost = balanceNumber(balance.overage_cost_usd);
  if (overage > 0 || overageCost > 0) {
    add(
      'Overage',
      [overage > 0 && formatBalance(overage, 'credits'), overageCost > 0 && formatBalance(overageCost, 'usd')]
        .filter(Boolean)
        .join(' · '),
      { warning: true },
    );
  }
  if (typeof balance.hard_limit_enforced === 'boolean') {
    add('Limit enforcement', balance.hard_limit_enforced ? 'Enforced' : 'Not enforced');
  }
  if (balance.window === 'rolling_30d') add('Usage window', 'Rolling 30 days');
  else if (balance.window === 'billing_period') add('Usage window', 'Billing period');
  if (isPlainObject(balance.breakdown)) {
    const top = Object.entries(balance.breakdown)
      .map(([name, value]) => [name, balanceNumber(value)])
      .filter(([, value]) => value != null && value > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([name, value]) => `${humanize(name)} ${formatBalance(value)}`);
    if (top.length) add('Top usage', top.join(' · '));
  }
  add('Provider data as of', formatDateTime(balance.last_updated));

  const ledger = balanceNumber(row.ledger_spend_usd);
  if (ledgerEnabled && ledger != null) {
    add(`Our AI ledger, ${windowDays} days`, formatBalance(ledger, 'usd'), {
      title: 'What our own AI spend ledger recorded for this provider over the history window',
    });
  }
  return details;
}

function ProviderCard({ item, windowDays, ledgerEnabled, refreshOff, now, refreshing, onRefresh }) {
  const { row, key, label, balance, lastCheck } = item;
  const color = FAMILY_COLORS[row.family] || FAMILY_COLORS[key] || FALLBACK_COLOR;
  const titleId = `provider-credits-${key}`;

  if (!item.configured) {
    return (
      <article
        aria-labelledby={titleId}
        className="flex flex-col rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 dark:border-slate-700 dark:bg-slate-900/40"
      >
        <CardHeader titleId={titleId} label={label} color={color} badges={item.badges} muted />
        <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
          Add the API key on the backend to track this provider.
        </p>
        {isHttpUrl(row.billing_url) && (
          <div className="mt-auto flex justify-end pt-4">
            <BillingLink href={row.billing_url} />
          </div>
        )}
      </article>
    );
  }

  const busy = refreshing === key;
  const headline = balanceHeadline(balance);
  const percent = usedPercent(balance);
  const limit = balanceNumber(balance?.limit);
  const remaining = balanceNumber(balance?.remaining);
  const isQuota = balance?.type === 'quota';
  const points = (Array.isArray(row.history) ? row.history : [])
    .map((point) => balanceNumber(point?.remaining))
    .filter((value) => value != null);
  const formatPoint = (value) => formatBalance(value, balance?.unit);
  const details = buildDetails(item, { windowDays, ledgerEnabled });
  const subtitle = balance?.type && balance.type !== 'unknown' ? balanceTypeLabel(balance.type) : null;
  const checkedAgo = formatRelativeTime(lastCheck?.created, now);
  const mode = lastCheck?.automatic === true ? ' · automatic' : lastCheck?.automatic === false ? ' · manual' : '';
  const checkedText = lastCheck ? `Checked ${checkedAgo || 'at an unknown time'}${mode}` : 'Never checked';
  const balanceAgo = formatRelativeTime(row.balance_checked_at, now);
  const border = item.low
    ? 'border-amber-300 ring-1 ring-amber-200 dark:border-amber-700/70 dark:ring-amber-900/40'
    : item.failed
      ? 'border-red-200 dark:border-red-900/60'
      : 'border-slate-200 dark:border-slate-700';

  return (
    <article
      aria-labelledby={titleId}
      aria-busy={busy}
      className={`flex flex-col rounded-2xl border bg-white p-5 shadow-sm dark:bg-slate-800 ${border}`}
    >
      <CardHeader titleId={titleId} label={label} color={color} subtitle={subtitle} badges={item.badges} />

      <div className="mt-5">
        {headline ? (
          <>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">{headline.label}</p>
            <p className="mt-1 flex flex-wrap items-baseline gap-x-2">
              <span
                className={`text-3xl font-bold tabular-nums tracking-tight ${item.low ? 'text-amber-700 dark:text-amber-300' : 'text-slate-950 dark:text-white'}`}
              >
                {headline.value}
              </span>
              {limit != null && remaining != null && (
                <span className="text-sm text-slate-500 dark:text-slate-400">of {formatBalance(limit, balance.unit)}</span>
              )}
            </p>
          </>
        ) : isQuota && percent != null ? (
          <>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Most-used quota</p>
            <p className="mt-1 flex flex-wrap items-baseline gap-x-2">
              <span className={`text-3xl font-bold tabular-nums tracking-tight ${item.low ? 'text-amber-700 dark:text-amber-300' : 'text-slate-950 dark:text-white'}`}>
                {formatPercentValue(percent)}
              </span>
              <span className="text-sm text-slate-500 dark:text-slate-400">
                used{balance.limiting_resource ? ` · ${humanize(balance.limiting_resource)}` : ''}
              </span>
            </p>
          </>
        ) : (
          <>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Balance</p>
            <p className="mt-1 text-2xl font-semibold text-slate-400 dark:text-slate-500">
              {item.notChecked ? '—' : 'Not reported'}
            </p>
            {item.notChecked && (
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Press Refresh to fetch the first balance.</p>
            )}
          </>
        )}
      </div>

      {percent != null && ((limit != null && limit > 0) || isQuota) && (
        <UsageBar
          percent={percent}
          label={`${label} usage`}
          low={item.low}
          // Email quotas list both windows in the details, so the caption would repeat one of them.
          caption={isQuota || balance.type === 'email_quota' ? null : formatUsedOf(balance.used, balance.limit, balance.unit)}
          showPercent={!isQuota}
        />
      )}

      {(remaining != null || points.length > 0) && (
        <div className="mt-4">
          <p className="text-xs text-slate-500 dark:text-slate-400">Balance, last {windowDays} days</p>
          <div className="mt-1 h-10" style={{ color }}>
            {points.length >= 2 ? (
              <Sparkline
                points={points}
                ariaLabel={`${label} balance history`}
                formatValue={formatPoint}
                className="h-10 w-full"
              />
            ) : (
              <p className="flex h-full items-center rounded-lg border border-dashed border-slate-200 px-3 text-xs text-slate-400 dark:border-slate-700 dark:text-slate-500">
                Not enough history for a trend yet
              </p>
            )}
          </div>
        </div>
      )}

      {details.length > 0 && (
        <dl className="mt-4 divide-y divide-slate-100 text-sm dark:divide-slate-700/60">
          {details.map(({ term, value, warning, title }) => (
            <div key={term} className="flex items-baseline justify-between gap-4 py-1.5">
              <dt className="shrink-0 text-slate-500 dark:text-slate-400">{term}</dt>
              <dd
                title={title}
                className={`min-w-0 text-right font-medium ${warning ? 'text-amber-700 dark:text-amber-300' : 'text-slate-800 dark:text-slate-100'}`}
              >
                {value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {balance?.note && <p className="mt-4 text-xs leading-5 text-slate-500 dark:text-slate-400">{balance.note}</p>}
      {item.failed && lastCheck?.detail && (
        <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs leading-5 text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
          {lastCheck.detail}
        </p>
      )}
      {item.failed && balance && balanceAgo && (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Showing the last known balance from {balanceAgo}.</p>
      )}
      {/* A failed check already explains why the balance is old. */}
      {item.stale && balance && !item.failed && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
          {refreshOff
            ? 'This balance is over a day old and automatic refresh is off. Press Refresh to update it.'
            : 'Balance is older than expected — the scheduled refresh may be failing or turned off.'}
        </p>
      )}

      <div className="mt-auto pt-4">
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4 dark:border-slate-700">
          <p className="text-xs text-slate-500 dark:text-slate-400" title={formatDateTime(lastCheck?.created) || undefined}>
            {checkedText}
          </p>
          <div className="flex items-center gap-1">
            {isHttpUrl(row.billing_url) && <BillingLink href={row.billing_url} />}
            <button
              type="button"
              aria-label={busy ? `Refreshing ${label}` : `Refresh ${label}`}
              disabled={Boolean(refreshing)}
              onClick={() => onRefresh(key)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <ArrowPathIcon className={`h-4 w-4 ${busy ? 'motion-safe:animate-spin' : ''}`} aria-hidden="true" />
              <SwapLabel busy={busy} idle="Refresh" active="Refreshing…" />
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function ProviderCredits() {
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [refreshed, setRefreshed] = useState(null);
  const [refreshing, setRefreshing] = useState(null);
  const [refreshError, setRefreshError] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const [denied, setDenied] = useState(null);
  const [, setClock] = useState(0);
  // The window selected right now. A refresh answers for the window it was
  // sent with; if the user has picked another one since, that answer is not shown.
  const daysRef = useRef(DEFAULT_DAYS);
  // Every GET takes a number when it starts and every refresh answer one when
  // it arrives, so a GET that started before a refresh finished never replaces it.
  const sequence = useRef(0);
  const lastLoadedAt = useRef(null);
  if (lastLoadedAt.current === null) lastLoadedAt.current = Date.now();

  const { data: loaded, error, loading, refetch } = useApiResource(
    ({ signal }) => {
      sequence.current += 1;
      const seq = sequence.current;
      return getProviderCredits({ days, signal }).then(
        (result) => {
          lastLoadedAt.current = Date.now();
          return { payload: result, seq };
        },
        (requestError) => {
          if (isAccessDenied(requestError)) setDenied({ detail: serverDetail(requestError) });
          throw requestError;
        },
      );
    },
    // Once the server has refused access, nothing is requested again.
    { deps: [days], enabled: !denied, errorMessage: 'Provider balances could not be loaded.' },
  );

  // Whichever is newer: the last GET or the last refresh answer. Derived on
  // render rather than copied in an effect, so there is no empty frame either.
  const latest = refreshed && (!loaded || refreshed.seq > loaded.seq) ? refreshed : loaded;
  const payload = latest?.payload ?? null;

  useEffect(() => {
    if (denied) return undefined;
    const visible = () => document.visibilityState !== 'hidden';
    const timer = setInterval(() => {
      if (visible()) refetch();
    }, POLL_INTERVAL_MS);
    // A hidden tab skips its polls; catch up as soon as it is shown again.
    const onVisibilityChange = () => {
      if (visible() && Date.now() - lastLoadedAt.current >= POLL_INTERVAL_MS) refetch();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [refetch, denied]);

  useEffect(() => {
    const timer = setInterval(() => setClock((value) => value + 1), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const items = useMemo(
    () => (Array.isArray(payload?.results) ? payload.results : []).filter(isPlainObject).map(describe),
    [payload],
  );
  const sorted = useMemo(() => sortItems(items), [items]);

  const refresh = async (provider) => {
    if (refreshing || denied) return;
    setRefreshing(provider || 'all');
    setRefreshError('');
    setAnnouncement('');
    try {
      const result = await refreshProviderCredits(provider ? [provider] : undefined, { days: daysRef.current });
      if (isPayload(result) && result.window_days === daysRef.current) {
        sequence.current += 1;
        setRefreshed({ payload: result, seq: sequence.current });
        lastLoadedAt.current = Date.now();
      }
      const fallbackLabel = provider ? items.find((item) => item.key === provider)?.label : null;
      setAnnouncement(refreshAnnouncement(result, provider, fallbackLabel));
      // Re-read the window selected now. This aborts any older GET still in
      // flight and clears the warning a failed poll may have left behind.
      refetch();
    } catch (requestError) {
      if (isAccessDenied(requestError)) {
        setDenied({ detail: serverDetail(requestError) });
      } else {
        setRefreshError(serverDetail(requestError) || 'Provider balances could not be refreshed. Try again in a minute.');
      }
    } finally {
      setRefreshing(null);
    }
  };

  const changeWindow = (event) => {
    const next = Number(event.target.value);
    daysRef.current = next;
    setDays(next);
  };

  const now = Date.now();
  const counts = summarize(items, payload?.summary);
  const lowLabels = sorted.filter((item) => item.low).map((item) => item.label);
  const interval = balanceNumber(payload?.refresh_interval_minutes);
  const refreshOff = !(interval > 0);
  const windowDays = balanceNumber(payload?.window_days) ?? days;
  const ledgerEnabled = payload?.ledger_enabled !== false;
  const latestCheckedAt = items.reduce((latestTime, item) => {
    const time = balanceTime(item.row.balance_checked_at);
    return time != null && (latestTime == null || time > latestTime) ? time : latestTime;
  }, null);
  const schedule = refreshOff
    ? 'Automatic refresh is off.'
    : `Balances refresh automatically every ${interval} minute${interval === 1 ? '' : 's'}.`;

  let body;
  if (denied) {
    body = <AccessDenied detail={denied.detail} />;
  } else if (!payload) {
    if (error) {
      body = <div className="mt-6"><ErrorState message={error} onRetry={refetch} /></div>;
    } else if (loading) {
      body = (
        <div className="flex min-h-72 items-center justify-center">
          <SmashLoader className="h-72" description="Loading provider balances" />
        </div>
      );
    } else {
      body = (
        <p className="mt-6 rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-500 dark:border-slate-700">
          No provider data was returned.
        </p>
      );
    }
  } else {
    body = (
      <>
        <section aria-label="Summary" className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            icon={ExclamationTriangleIcon}
            tone={counts.low ? 'amber' : 'slate'}
            label="Low balance"
            value={String(counts.low)}
            detail={`of ${counts.configured} configured`}
          />
          <StatCard
            icon={XCircleIcon}
            tone={counts.failed ? 'red' : 'slate'}
            label="Check failed"
            value={String(counts.failed)}
            detail="on the latest check"
          />
          <StatCard
            icon={NoSymbolIcon}
            tone="slate"
            label="Balance unavailable"
            value={String(counts.unavailable)}
            detail="not exposed or not fetched"
          />
          <StatCard
            icon={ClockIcon}
            tone={counts.stale ? 'amber' : 'blue'}
            label="Last refreshed"
            value={latestCheckedAt != null ? humanize(formatRelativeTime(latestCheckedAt, now)) : 'Never'}
            detail={counts.stale ? `${counts.stale} stale` : formatDateTime(latestCheckedAt) || 'No balance stored yet'}
          />
        </section>

        {lowLabels.length > 0 && (
          <div
            role="status"
            className="mt-6 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200"
          >
            <WarningIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <div>
              <p className="font-semibold">{`Low balance: ${lowLabels.join(', ')}`}</p>
              <p className="mt-0.5 text-sm">Top up or raise the limit before these run out.</p>
            </div>
          </div>
        )}

        {sorted.length ? (
          <section aria-label="Providers" className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {sorted.map((item) => (
              <ProviderCard
                key={item.key}
                item={item}
                windowDays={windowDays}
                ledgerEnabled={ledgerEnabled}
                refreshOff={refreshOff}
                now={now}
                refreshing={refreshing}
                onRefresh={refresh}
              />
            ))}
          </section>
        ) : (
          <p className="mt-6 rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-500 dark:border-slate-700">
            No providers are registered on the backend.
          </p>
        )}
      </>
    );
  }

  return (
    <main className="mx-auto max-w-7xl px-4 py-8 md:px-8">
      <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-blue-600">Platform administration</p>
          <h1 className="mt-1 text-4xl font-bold text-slate-950 dark:text-white">Provider credits</h1>
          <p className="mt-2 max-w-3xl text-slate-600 dark:text-slate-300">
            What is left on every paid platform we use, with history, burn rate and a runway forecast.
            {payload && !denied ? ` ${schedule}` : ''} Opening this page never contacts a provider.
          </p>
        </div>
        {!denied && (
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label
                htmlFor="provider-credits-window"
                className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
              >
                History window
              </label>
              <select
                id="provider-credits-window"
                value={days}
                onChange={changeWindow}
                className="mt-1 rounded-xl border border-slate-300 bg-white py-2.5 pl-3 pr-9 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              >
                {WINDOW_OPTIONS.map((option) => (
                  <option key={option} value={option}>Last {option} days</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => refresh()}
              disabled={Boolean(refreshing)}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <ArrowPathIcon className={`h-4 w-4 ${refreshing === 'all' ? 'motion-safe:animate-spin' : ''}`} aria-hidden="true" />
              <SwapLabel busy={refreshing === 'all'} idle="Refresh all" active="Refreshing…" />
            </button>
          </div>
        )}
      </div>

      {/* Reserved height: background updates never push the page down. */}
      <div className="mt-3 flex min-h-[1.25rem] flex-wrap items-center gap-x-3 text-xs text-slate-500 dark:text-slate-400">
        <span aria-hidden={!(loading && payload)} className={loading && payload ? '' : 'invisible'}>Updating…</span>
        {error && payload && !denied && (
          <span className="text-amber-700 dark:text-amber-300">Could not update: {error} Showing the last loaded data.</span>
        )}
      </div>
      <p className="sr-only" aria-live="polite">{announcement}</p>

      {refreshError && (
        <p role="alert" className="mt-2 rounded-xl border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
          {refreshError}
        </p>
      )}

      {body}
    </main>
  );
}
